import React, { useEffect, useMemo, useState } from 'react';
import {
  deleteLeftMenu,
  deleteLeftMenuItem,
  getLeftMenuItems,
  getLeftMenus,
} from '../../../api/managerApi';
import LeftMenuCreate from './LeftMenuCreate';
import LeftMenuDetail from './LeftMenuDetail';
import LeftMenuEdit from './LeftMenuEdit';
import LeftMenuItemCreate from './menuItem/LeftMenuItemCreate';
import LeftMenuItemDetail from './menuItem/LeftMenuItemDetail';
import LeftMenuItemEdit from './menuItem/LeftMenuItemEdit';
import './leftMenu.css';

function LeftMenuList() {
  const MENU_TREE_HEIGHT = 520;
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState([]);
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState({ mode: 'empty', menuId: null, itemId: null });

  const loadData = async () => {
    setLoading(true);
    try {
      const [menuRows, itemRows] = await Promise.all([getLeftMenus(), getLeftMenuItems()]);
      setMenus(Array.isArray(menuRows) ? menuRows : []);
      setItems(Array.isArray(itemRows) ? itemRows : []);
    } catch (err) {
      console.error('좌측 메뉴/하위항목 조회 실패:', err);
      alert('좌측 메뉴 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const sortedMenus = useMemo(
    () => [...menus].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.menu_id || 0) - (b.menu_id || 0)),
    [menus]
  );

  const itemsByMenu = useMemo(() => {
    const grouped = {};
    items.forEach(item => {
      const key = item.menu_id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.item_id || 0) - (b.item_id || 0));
    });
    return grouped;
  }, [items]);

  const selectedMenu = useMemo(
    () => menus.find(m => String(m.menu_id) === String(panel.menuId)),
    [menus, panel.menuId]
  );

  const selectedItem = useMemo(
    () => items.find(i => String(i.item_id) === String(panel.itemId)),
    [items, panel.itemId]
  );

  const handleDeleteMenu = async menu => {
    if (!window.confirm(`메뉴를 삭제하시겠습니까? (${menu.label})`)) return;
    await deleteLeftMenu(menu.menu_id);
    await loadData();
    setPanel({ mode: 'empty', menuId: null, itemId: null });
  };

  const handleDeleteItem = async item => {
    if (!window.confirm(`하위메뉴를 삭제하시겠습니까? (${item.label})`)) return;
    await deleteLeftMenuItem(item.item_id);
    await loadData();
    setPanel({ mode: 'menu-detail', menuId: item.menu_id, itemId: null });
  };

  const typeClass = type => {
    const map = { link: 'mgr-badge-type-link', url: 'mgr-badge-type-url', category: 'mgr-badge-type-category', action: 'mgr-badge-type-action' };
    return map[type] || '';
  };

  const renderRightPanel = () => {
    switch (panel.mode) {
      case 'empty':
        return (
          <div className="mgr-right-empty">
            <span style={{ fontSize: '1.8rem' }}>☰</span>
            <span>메뉴를 선택하세요.</span>
          </div>
        );
      case 'menu-create':
        return (
          <LeftMenuCreate
            onSuccess={async created => {
              await loadData();
              setPanel({ mode: 'menu-detail', menuId: created.menu_id, itemId: null });
            }}
            onCancel={() => setPanel({ mode: 'empty', menuId: null, itemId: null })}
          />
        );
      case 'menu-detail':
        return (
          <LeftMenuDetail
            menu={selectedMenu}
            onEdit={menu => setPanel({ mode: 'menu-edit', menuId: menu.menu_id, itemId: null })}
            onDelete={handleDeleteMenu}
            onCreateItem={menu => setPanel({ mode: 'item-create', menuId: menu.menu_id, itemId: null })}
          />
        );
      case 'menu-edit':
        return (
          <LeftMenuEdit
            menuId={panel.menuId}
            onSuccess={async updated => {
              await loadData();
              setPanel({ mode: 'menu-detail', menuId: updated.menu_id, itemId: null });
            }}
            onCancel={() => setPanel({ mode: 'menu-detail', menuId: panel.menuId, itemId: null })}
          />
        );
      case 'item-create':
        return (
          <LeftMenuItemCreate
            menuId={panel.menuId}
            onSuccess={async created => {
              await loadData();
              setPanel({ mode: 'item-detail', menuId: created.menu_id, itemId: created.item_id });
            }}
            onCancel={() => setPanel({ mode: 'menu-detail', menuId: panel.menuId, itemId: null })}
          />
        );
      case 'item-detail':
        return (
          <LeftMenuItemDetail
            item={selectedItem}
            onEdit={item => setPanel({ mode: 'item-edit', menuId: item.menu_id, itemId: item.item_id })}
            onDelete={handleDeleteItem}
          />
        );
      case 'item-edit':
        return (
          <LeftMenuItemEdit
            itemId={panel.itemId}
            onSuccess={async updated => {
              await loadData();
              setPanel({ mode: 'item-detail', menuId: updated.menu_id, itemId: updated.item_id });
            }}
            onCancel={() => setPanel({ mode: 'item-detail', menuId: panel.menuId, itemId: panel.itemId })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mgr-page container-fluid p-0 mt-3">
      <div className="mgr-header">
        <h5 className="mgr-header-title">좌측 메뉴 관리</h5>
        <button className="btn btn-primary btn-sm" onClick={() => setPanel({ mode: 'menu-create', menuId: null, itemId: null })}>
          + 메뉴 생성
        </button>
      </div>

      {loading ? (
        <div className="mgr-loading">로딩 중...</div>
      ) : (
        <div className="mgr-split">
          {/* Left panel */}
          <div className="mgr-left">
            <div className="mgr-left-header">메뉴 트리</div>
            {sortedMenus.length === 0 ? (
              <div className="mgr-empty-list">등록된 메뉴가 없습니다.</div>
            ) : (
              <ul
                className="mgr-list"
                style={{ height: `${MENU_TREE_HEIGHT}px`, overflowY: 'auto', flex: '0 0 auto' }}
              >
                {sortedMenus.map(menu => (
                  <li key={menu.menu_id} className="mgr-list-item">
                    <div
                      className={`mgr-list-item-row${String(panel.menuId) === String(menu.menu_id) && !panel.itemId ? ' selected' : ''}`}
                      onClick={() => setPanel({ mode: 'menu-detail', menuId: menu.menu_id, itemId: null })}
                    >
                      <span className="mgr-list-item-label">{menu.label}</span>
                      <span className={`mgr-badge ${typeClass(menu.type)}`}>{menu.type}</span>
                    </div>
                    {(itemsByMenu[menu.menu_id] || []).length > 0 && (
                      <ul className="mgr-list-sub">
                        {itemsByMenu[menu.menu_id].map(item => (
                          <li key={item.item_id}>
                            <div
                              className={`mgr-list-sub-row${String(panel.itemId) === String(item.item_id) ? ' selected' : ''}`}
                              onClick={() => setPanel({ mode: 'item-detail', menuId: menu.menu_id, itemId: item.item_id })}
                            >
                              <span style={{ marginRight: 6, color: '#adb5bd' }}>└</span>
                              {item.label}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right panel */}
          <div className="mgr-right">
            {renderRightPanel()}
          </div>
        </div>
      )}
    </div>
  );
}

export default LeftMenuList;
